'use client'

import * as Sentry from '@sentry/nextjs'
import { ReactNode, Suspense, useEffect } from 'react'
import classNames from 'classnames'
import Lottie from 'react-lottie-player/dist/LottiePlayerLight'
import TXT from '@/messages'
import { updateBodyClass } from '@/utils/dom.utils'
import { StoreProvider } from '@/app/_providers/StoreProvider'
import QueryProvider from '@/app/_providers/QueryProvider/QueryProvider'
import LoginProvider from '@/app/_providers/LoginProvider/LoginProvider'
import { ELEMENT_IDS } from '@/config/html-attributes.config'
import { Header } from '@/app/_components/Header/Header'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import styles from './ErrorPage.module.css'
import Footer from '@/app/_components/Footer/Footer'
import animationData from '@/config/lotties/error-blob.json'
import { AnchorButton } from '@/app/_components/AnchorButton'
import { Button } from '@/app/_components/Button'
import Error from 'next/error'
import useIsHydrated from '@/hooks/useIsHydrated'

/**
 * Shared fallback UI for every error boundary and the 404 page.
 *
 * Renders the branded "empty portal" page (header, Lottie blob, footer) inside its own
 * provider stack so it can stand alone when a route's providers have failed. The default
 * copy describes a 404; route-level boundaries override it for more specific scenarios.
 *
 * @param props.error    The error forwarded by a Next.js error boundary. When present it is
 *                       reported to Sentry. Omitted for the static 404 (`not-found.tsx`).
 * @param props.onRetry  Recovery callback (Next.js `unstable_retry`). When provided, a
 *                       "Try Again" button is shown that re-fetches and re-renders the segment.
 * @param props.header   Optional heading override (e.g. confirmation-specific messaging).
 * @param props.text     Optional body-copy override paired with `header`.
 */
const ErrorPageComponent = ({
  error,
  onRetry,
  header: headerOverride,
  text: textOverride,
  withHeader = false,
  withFooter = false,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  header?: ReactNode;
  text?: ReactNode;
  withHeader?: boolean
  withFooter?: boolean
}): ReactNode => {
  const isHydrated = useIsHydrated()

  const { pages: { error: { header: defaultHeader, text: defaultText } } } = TXT
  const header = headerOverride ?? defaultHeader
  const text = textOverride ?? defaultText

  useEffect(() => {
    if (!error) return
    Sentry.captureException(error)
  }, [error])

  useEffect(() => {
    // TODO: remove this when there is broad browsers support for the :has selector
    // body:has(.error-page)
    updateBodyClass('error-page')
    return () => {
      updateBodyClass(null, 'error-page')
    }
  }, [])

  if (!isHydrated) {
    return null
  }

  return (
    <StoreProvider prefix="/">
      <QueryProvider>
        <LoginProvider>
          {withHeader && (
            <div id={ELEMENT_IDS.STICKY_HEADER} data-testid={ELEMENT_IDS.STICKY_HEADER}>
              <Header
                portals={{
                  cartButton: PORTAL_SELECTORS.cartButton,
                  landingPageButton: PORTAL_SELECTORS.ticketsButton,
                  skipToMain: PORTAL_SELECTORS.skipToMain,
                  authButton: PORTAL_SELECTORS.authMenu,
                }}
              />
            </div>
          )}
          <div className={classNames(styles.PageContainer, 'error-page')}>
            <div className={styles.LottieContainer} data-testid="error-blob-container">
              <Lottie
                loop
                play
                animationData={animationData}
              />
            </div>
            <div className={styles.ContentContainer}>
              <h1 className={styles.Header} data-testid="error-page-header">{header}</h1>
              <div className={styles.MessageContainer} data-testid="error-page-text">
                {text}
              </div>
              {onRetry && (
                <div className={styles.SellerContainer}>
                  <div className={styles.ButtonContainer}>
                    <Button onClick={onRetry} testId="error-page-retry">
                      {TXT.buttons.tryAgain}
                    </Button>
                  </div>
                </div>
              )}
              <div className={styles.SellerContainer}>
                <AnchorButton href={'/'}>{TXT.siteChrome.getMeOutOfHere}</AnchorButton>
              </div>
            </div>
          </div>
          {withFooter && (
            <Suspense><Footer /></Suspense>
          )}
        </LoginProvider>
      </QueryProvider>
    </StoreProvider>
  )
}

export default ErrorPageComponent
